import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { LocaleService } from '@maximizer/core/shared/data-access';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { formatCitationDate } from '../pipes/crm-citation-ref.pipe';
import {
  CrmCitation,
  CrmCitationRecord,
  crmCitationIcon,
  crmCitationTypeKey,
} from './crm-citation.model';

/**
 * The card a citation chip opens on hover or focus: the record's type, date, subject, who it is
 * for, its first lines and the action that opens it. With no record, what the citation carries.
 */
@Component({
  selector: 'ai-crm-citation-card',
  templateUrl: './crm-citation-card.component.html',
  styleUrl: './crm-citation-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
})
export class CrmCitationCardComponent {
  private readonly locale = inject(LocaleService);
  private readonly translate = inject(TranslateService);

  readonly citation = input.required<CrmCitation>();
  readonly record = input<CrmCitationRecord | null>(null);
  readonly opened = output<void>();

  protected readonly icon = computed(() => crmCitationIcon(this.citation()));

  protected readonly kind = computed(
    () =>
      this.record()?.interaction ||
      this.translate.instant(crmCitationTypeKey(this.citation().type)),
  );

  protected readonly date = computed(() => {
    const record = this.record();
    return formatCitationDate(
      this.locale,
      record?.date ?? this.citation().date,
      record?.hasTime ?? false,
    );
  });

  protected readonly label = computed(() => {
    const subject = this.record()?.subject;
    return subject ? `${this.kind()}: ${subject}` : this.kind();
  });
}
